import type { BookingCalendarDTO } from "../contracts/bookings";
import {
  isMultiMemberPackageKind,
  multiMemberPackageLabel,
} from "../lib/package-kind";
import type { TimetableItem } from "@/modules/public-booking/domain/contracts/public-booking";

import type { OffSiteDistance } from "./off-site-distance";
import { offSiteDistanceFullLabel } from "./off-site-distance";

export type PrivateLessonLocationFields = {
  locationKind?: "studio" | "off_site" | null;
  offSiteDistance?: OffSiteDistance | null;
  offSiteLabel?: string | null;
  roomName?: string | null;
  roomId?: string | null;
};

/** ຂໍ້ຄວາມສະຖານທີ່ສຳລັບຕາຕະລາງ / ລາຍລະອຽດ */
export function formatPrivateLessonBookingPlace(
  b: PrivateLessonLocationFields,
): string {
  if (b.locationKind === "off_site") {
    const dist = offSiteDistanceFullLabel(b.offSiteDistance);
    const t = b.offSiteLabel?.trim();
    return t ? `${dist}: ${t}` : dist;
  }
  return b.roomName?.trim() || "—";
}

/** ຫົວຂໍ້ event ປະຕິທິນ — ນັກຮຽນ · ຄູ · ສະຖານທີ່ (ຖ້ານອກສະຖານທີ່) */
export function formatPrivateLessonCalendarTitle(
  b: Pick<
    BookingCalendarDTO,
    | "studentName"
    | "locationKind"
    | "offSiteDistance"
    | "offSiteLabel"
    | "instructorNames"
    | "packageKind"
    | "groupSize"
  >,
): string {
  let title = b.studentName?.trim() || "ວິຊາສ່ວນຕົວ";
  if (isMultiMemberPackageKind(b.packageKind)) {
    const prefix = multiMemberPackageLabel(b.packageKind) ?? "ກຸ່ມ";
    const n = b.groupSize != null ? ` (${b.groupSize} ຄົນ)` : "";
    title = `${prefix}${n}: ${title}`;
  }
  const instructors = (b.instructorNames ?? []).filter(Boolean).join(", ");
  if (instructors) {
    title = `${title} · ${instructors}`;
  }
  if (b.locationKind === "off_site") {
    const dist = offSiteDistanceFullLabel(b.offSiteDistance);
    const place = b.offSiteLabel?.trim();
    title = place ? `${title} · ${dist}: ${place}` : `${title} · ${dist}`;
  }
  return title;
}

/** ຂໍ້ຄວາມສຳລັບ audit / change log */
export function formatPrivateLessonAuditLocation(
  b: PrivateLessonLocationFields,
): string {
  if (b.locationKind === "off_site") {
    const dist = offSiteDistanceFullLabel(b.offSiteDistance);
    const t = b.offSiteLabel?.trim();
    return t ? `${dist}: ${t}` : dist;
  }
  const r = b.roomName?.trim() || b.roomId || "";
  return r ? `ສະຕູດິໂອ: ${r}` : "ສະຕູດິໂອ";
}

/** Map BookingCalendarDTO → TimetableItem (ໃຊ້ຮ່ວມກັນທັງ admin calendar ແລະ public timetable) */
export function mapBookingCalendarToTimetableItem(
  b: BookingCalendarDTO,
  offSiteTimetableRoomId: string,
): TimetableItem {
  const isOff = b.locationKind === "off_site";
  return {
    type: "private",
    id: b.id,
    roomId: isOff ? offSiteTimetableRoomId : (b.roomId ?? ""),
    roomName: formatPrivateLessonBookingPlace(b),
    date: b.date,
    startTime: b.startTime,
    endTime: b.endTime,
    title: formatPrivateLessonCalendarTitle(b),
    status: b.status,
    instructorNames: (b.instructorNames ?? []).filter(Boolean),
  };
}
